// Singleton TTS audio element + iOS unlock for the AI interview.
//
// Problem: iOS Safari blocks HTMLAudioElement.play() unless the call happens
// synchronously inside a user gesture. The interview fetches each spoken prompt
// asynchronously (`const blob = await tts(text)`) and only then calls play(), so
// by that point the gesture has expired and iOS rejects it -> the interviewer's
// voice is silent on iPhone (desktop ignores the policy, so it worked there).
//
// Fix: use ONE persistent <audio> element and "bless" it by playing a tiny
// silent clip DURING the Start gesture (primeTtsAudio). Once a media element has
// been started by a user gesture, iOS allows subsequent programmatic play() on
// that SAME element — even after awaits. speak() then reuses getTtsAudio()
// instead of `new Audio()`.
//
// The element is module-level so it survives the instruction -> interview
// component switch (the gesture is in instruction.js, playback is in
// interview.js).

let audioEl = null;
let unlocked = false;

// ~0.12s of 8-bit/8kHz silence. Tiny, decodes instantly, no network.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRuQDAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YcADAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIA=';

export const getTtsAudio = () => {
  if (!audioEl && typeof Audio !== 'undefined') {
    audioEl = new Audio();
    audioEl.preload = 'auto';
    // Inline playback hint (harmless for audio; matches our iOS posture).
    try { audioEl.setAttribute('playsinline', 'true'); } catch (_) {}
  }
  return audioEl;
};

// Call SYNCHRONOUSLY inside a user gesture (Start / Accept buttons) to unlock
// audio playback on iOS Safari. Idempotent and safe to call off-gesture.
export const primeTtsAudio = () => {
  const a = getTtsAudio();
  if (!a || unlocked) return;
  try {
    a.muted = true;
    a.src = SILENT_WAV;
    const restore = () => {
      try { a.pause(); a.currentTime = 0; } catch (_) {}
      a.muted = false;
      unlocked = true;
    };
    const p = a.play();
    if (p && typeof p.then === 'function') {
      p.then(restore).catch(() => { a.muted = false; });
    } else {
      restore();
    }
  } catch (_) {
    a.muted = false;
  }
};

export const isTtsAudioUnlocked = () => unlocked;
